import React, { useContext } from "react";
import { RESOURCE_NAME } from "common/authorization";
import { OBJECT_TYPE } from "common/constants";
import { buildLink } from "common/routing";
import {
  isCustomMapGeometry,
  type TargetObject,
} from "common-client/utils/customObject";
import { useGetTaskLazyQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { formatDateString } from "../../utils/dates";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { Actions } from "../Common/ActionCell";
import { Body } from "../Common/Typography";
import type { ActionsProps } from "../Inputs/DropdownMenu";
import { type Task } from "./__queries__";
import { TaskContentContainer, TaskItemContainer } from "./__styles__/TaskItem";
import { useEditTaskModal } from "./Form/EditTaskForm";
import { StatusTag } from "./StatusTag";
import { useDeleteTaskWithConfirmation } from "./useDeleteTaskWithConfirmation";

type Props = {
  task: Task;
  targetObject?: Maybe<TargetObject>;
};

export function TaskActions({ task, targetObject }: Props) {
  const { authorized } = useContext(AuthContext);
  const [showTaskModal] = useEditTaskModal({
    taskId: task.id,
  });
  const deleteTaskWithConfirmation = useDeleteTaskWithConfirmation({ task });

  const [getTask] = useGetTaskLazyQuery();

  const canViewSubmissions = authorized({
    resource: RESOURCE_NAME.SUBMISSION,
    permission: "view",
  });

  const canDeleteTask = authorized({
    resource: RESOURCE_NAME.TASK,
    permission: "delete",
  });

  const actions: Array<ActionsProps> = [
    { label: "View", onClick: showTaskModal },
    {
      label: "Delete",
      onClick: () => deleteTaskWithConfirmation(),
      disabled: !canDeleteTask,
      variant: "red",
    },
  ];

  if (task.targetSubmissionType) {
    const hasRelatedSubmission = task.relatedSubmission;

    if (hasRelatedSubmission) {
      actions.unshift({
        label: "View related record",
        href: buildLink("viewSubmission", {
          submissionId: hasRelatedSubmission.id,
        }),
        target: "_blank",
        disabled: !canViewSubmissions,
      });
    } else {
      const objectType =
        isCustomMapGeometry(targetObject) ?
          OBJECT_TYPE.CUSTOM_MAP_GEOMETRY
        : OBJECT_TYPE.PROPERTY;

      actions.unshift({
        label: `Add ${task.targetSubmissionType.name}`,
        href: buildLink(
          "createSubmission",
          {
            submissionTypeId: task.targetSubmissionType.id,
            objectId: targetObject?.id,
          },
          {
            taskId: task.id,
            objectType,
          },
        ),
        target: "_blank",
        disabled: !task.targetSubmissionType.currentUserCanCreate,
      });
    }
  }
  return (
    <Actions
      actions={actions}
      onMenuDidOpen={() => {
        void getTask({ variables: { id: task.id } });
      }}
    />
  );
}

export function TaskItem({ task, targetObject }: Props) {
  return (
    <TaskItemContainer>
      <TaskContentContainer>
        <FlexColumn css={{ rowGap: spacing.xs }}>
          <Body size="default" type="emphasis">
            {task.name}
          </Body>
          <Body size="small" type="regular" color="contentDisabled">
            Last edited{" "}
            {formatDateString({
              dateString: task.updatedAt,
              format: "MM/DD/YYYY",
            })}
          </Body>
        </FlexColumn>
        <StatusTag status={task.status} />
      </TaskContentContainer>
      <TaskActions task={task} targetObject={targetObject} />
    </TaskItemContainer>
  );
}
